import { Col, Container, Nav, Row } from "react-bootstrap";
import { Button } from "./Controls/Button";
import { FacilityListEntry } from "./Controls/FacilityListEntry";
import { Link, useLoaderData, useNavigate } from "react-router-dom";
import { InfoWindow, Map, AdvancedMarker, useMap } from '@vis.gl/react-google-maps';
import { useEffect, useState } from "react";
import { FacilityLite, Operator } from "./Data/ApiTypes";
import { Hyperlink } from "./Controls/Hyperlink";
import { List, Speedometer2 } from "react-bootstrap-icons";
import { LocationInput } from "./Controls/LocationInput";
import { navigationService } from "./services/NavigationService";
import { useAuthorization } from "./hooks/useAuthorization";
interface MapHandlerProps {
    place: google.maps.places.Place | null;
}

const MapHandler = ( { place }: MapHandlerProps ) => {
    const map = useMap();

    useEffect( () => {
        if( !map || !place ) {
            return;
        }
        if( place?.viewport ) {
            map.fitBounds( place.viewport );
        }
    }, [map, place] );

    return null;
};

export function FacilityListPage() {
    const map = useMap();
    const nav = useNavigate();
    const { canAccessPath } = useAuthorization();
    const [facilities, op] = useLoaderData() as [FacilityLite[], Operator];
    const [selectedFacility, setSelectedFacility] = useState<FacilityLite>();
    const [place, setPlace] = useState<google.maps.places.Place | null>(null);
    const navToFacility = (facilityId: number) => {
        navigationService.navigateToFacilityFromMap(nav, facilityId);
    };

    useEffect(() => {
        // Record that user is on map view
        navigationService.setLastFacilitiesView('map');
    }, []);

    useEffect(() => {
        if (!map || facilities.length === 0) return;
      
        // Delay fitBounds slightly to allow markers to render
        const timeout = setTimeout(() => {
          const bounds = new google.maps.LatLngBounds();
          facilities.forEach(facility => bounds.extend(facility.coordinates));
      
          map.fitBounds(bounds, {
            top: 60,
            bottom: 60,
            left: 60,
            right: 60,
          });
        }, 300); 
      
        return () => clearTimeout(timeout);
      }, [map, facilities]);

    return <Container fluid className="fullheight">
        <Row className="fullheight">
            <Col md={7} lg={6} xl={5} xxl={4} className="pt-2 scroll-y fullheight">
                {canAccessPath('/operator/facilities') && (
                <Button className="w-100 mb-2" onClick={() => navigationService.navigateToFacilitiesGrid(nav)}>
                    <List  className="icon-shift-up me-2" />Back to List View
                </Button>
                )}
                <LocationInput
                    name={'Location'}
                    onPlaceSelect={ ( pl ) => setPlace( pl ) } />
                
                {facilities.map( f =>
                    <FacilityListEntry key={f.facilityId}
                                       facility={f}
                                       className="border"
                                       onClick={ () => navToFacility( f.facilityId ) }  /> )}
            </Col>
            <Col className="d-none d-md-block fullheight p-0">
                <Map defaultCenter={{ lat: 39.299236, lng: -76.609383 }}
                    defaultZoom={13}
                    mapId="6d064567c51cdbc9">
                    {facilities.map( f => {
                        function clickFacility( ev: any ) {
                            setSelectedFacility( f );
                        }
                        if( f === selectedFacility ) {
                            return <InfoWindow key={f.facilityId}
                                               onClose={() => setSelectedFacility( undefined )}
                                               zIndex={1000}
                                               headerDisabled
                                               position={selectedFacility.coordinates}>
                                <div className="p-1" style={{ display: "grid", gridTemplateColumns: "auto auto", gap: "0.5em" }}>
                                    <div className="fw-bold fs-6 grid-col-1">
                                        {selectedFacility.name}
                                    </div>
                                    <div className="grid-col-1 text-start text-primary text-decoration-underline">
                                        <Hyperlink onClick={ () => navToFacility( selectedFacility.facilityId ) }>
                                            Manage Facility
                                        </Hyperlink>
                                    </div>
                                </div>
                            </InfoWindow>
                        }
                        const icon = {
                            url:         "/lot-pin.png",
                            labelOrigin: new google.maps.Point( 22, 18 ),
                            scaledSize:  new google.maps.Size(  54, 68 )
                        };
                        return <AdvancedMarker
                        key={f.facilityId}
                        position={f.coordinates}
                        onClick={() => clickFacility(f)}
                        zIndex={Number(f.facilityId)}
                        >
                        <img
                            src={icon.url}
                            style={{
                            transform: 'translate(-50%, -100%)',
                            width: icon.scaledSize.width,
                            height: icon.scaledSize.height,
                            }}
                            alt="Facility marker"
                        />
                        </AdvancedMarker>
                    } )}
                    <MapHandler place={place} />
                </Map>
            </Col>
        </Row>
    </Container>;
}